import React, { useState } from "react";
import { useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import { Column, Row, Text, Icon } from "@amzn/meridian";
import Textarea from "@amzn/meridian/textarea";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import { InspectionResult, ComplianceFixWhen } from "src/services/inspectionService";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { fixInspectionResult, rememberReason } from "src/store/inspectionResultsSlice";
import useKeyboardScrollReset from "src/hooks/useKeyboardScrollReset";
import ReasonTag from "./ReasonTag";
import { pickReuseReasons } from "./reasonTags";
import "./fixItLaterPage.scss";

/**
 * "Fix it later" page for a non-compliant result: the finding is real but cannot be
 * corrected on site. Confirming creates an open AUSTIN finding/action and leaves the
 * checkpoint non-compliant (unlike a fix-it-now, this does not change compliance).
 */
const FixItLaterPage: React.FC = () => {
    const { inspectionId } = useParams<{ inspectionId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const [reason, setReason] = useState<string>("");
    const [reasonTouched, setReasonTouched] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    useKeyboardScrollReset();

    const suggestedResolutionReasons = useAppSelector((rootState) => rootState.inspectionResults.suggestedResolutionReasons);
    const reuseReasons = pickReuseReasons(suggestedResolutionReasons);

    const state = location.state as {
        result: InspectionResult;
        questionText: string;
        objectCheck?: { object_name: string };
    } | null;

    if (!state) {
        return <Navigate to={`/configurationDrivenInspections/${inspectionId}/review`} replace />;
    }

    const { result, questionText, objectCheck } = state;

    const trimmedReason = reason.trim();
    const hasReason = trimmedReason !== "";

    const canSubmit = hasReason;

    const handleReuseTagTap = (tagText: string) => {
        setReason(tagText);
        setReasonTouched(true);
        setSubmitError(null);
    };

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setIsSubmitting(true);
        setSubmitError(null);

        const formData = new FormData();
        formData.append("when", ComplianceFixWhen.LATER);
        formData.append("result_id", result.resultId);
        formData.append("comment", trimmedReason);
        if (objectCheck) {
            formData.append("object_name", objectCheck.object_name);
        }

        try {
            await dispatch(fixInspectionResult({
                inspection_id: inspectionId!,
                question_id: result.questionId,
                formData,
            })).unwrap();

            dispatch(rememberReason(trimmedReason));

            navigate(-1);
        } catch (error) {
            console.error("Failed to submit fix-later:", error);
            const message =
                typeof error === "string"
                    ? error
                    : error instanceof Error
                        ? error.message
                        : "Failed to create the open action. Please try again.";
            setSubmitError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fix-it-later-page pwa-scrollable">
            <Column spacingInset="500" spacing="500">
                <Row alignmentVertical="center" spacing="300">
                    <div
                        className="fix-it-later-page__back"
                        onClick={() => navigate(-1)}
                        role="button"
                    >
                        <Icon tokens={arrowLeftTokens} color="success" />
                    </div>
                    <Text type="b300" color="primary">
                        Fix it later
                    </Text>
                </Row>

                <div className="fix-it-later-page__info-banner">
                    <Text type="b200" color="primary">
                        Use this only when the finding is real but cannot be corrected right now. An
                        AUSTIN finding and action will be created and left open until it is resolved.
                    </Text>
                </div>

                <p className="fix-it-later-page__question-text">{questionText}</p>

                <Column spacing="300">
                    <Text type="h100" color="success">Reason this cannot be fixed *</Text>
                    {reuseReasons.length > 0 && (
                        <Row spacing="300" wrap="down">
                            {reuseReasons.map((reason) => (
                                <ReasonTag
                                    key={reason}
                                    text={reason}
                                    variant="dashed"
                                    selected={false}
                                    onTap={handleReuseTagTap}
                                />
                            ))}
                        </Row>
                    )}
                    <Textarea
                        value={reason}
                        onChange={(value: string) => {
                            setReason(value);
                            setReasonTouched(true);
                            setSubmitError(null);
                        }}
                        onBlur={() => setReasonTouched(true)}
                        placeholder="Describe why this cannot be corrected on site..."
                        rows={4}
                        error={reasonTouched && !hasReason}
                        errorMessage={
                            reasonTouched && !hasReason
                                ? "A reason is required to create an open action."
                                : undefined
                        }
                    />
                </Column>

                <Text type="b100" color="secondary">
                    Owner defaults to you and can be reassigned. The EAP program team recommends closing the action as soon as possible.
                </Text>

                <Text type="b100" color="secondary">
                    Confirming creates the AUSTIN finding and action, keeps this checkpoint Non
                    Compliant, and unlocks its Verified checkbox. You still tick Verified yourself.
                </Text>

                {submitError && (
                    <Text type="b100" color="error">{submitError}</Text>
                )}
            </Column>

            <button
                className={`fix-it-later-page__submit-btn ${canSubmit ? "fix-it-later-page__submit-btn--enabled" : ""}`}
                onClick={handleSubmit}
                disabled={!canSubmit || isSubmitting}
            >
                {isSubmitting ? "SUBMITTING..." : "CONFIRM – CREATE OPEN ACTION"}
            </button>
        </div>
    );
};

export default FixItLaterPage;
